import dayjs from "dayjs";
import {
  buscarVariante,
  idDeAdicional,
  precioDesde,
  rangoDeDuracion,
  tieneAdicionales,
  tieneVariantes,
} from "../../helpers/beautyLineas";

// Pure domain helpers for the public Beauty (turnos) booking flow. Everything
// here is derived from what the back returns; nothing is invented.

const WEEKDAYS_LONG = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const WEEKDAYS_SHORT = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
const MONTHS_LONG = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
// Opening-hours table starts on Monday; weekday 0 is Sunday.
const WEEK_ROWS = [
  { weekday: 1, label: "Lunes" },
  { weekday: 2, label: "Martes" },
  { weekday: 3, label: "Miércoles" },
  { weekday: 4, label: "Jueves" },
  { weekday: 5, label: "Viernes" },
  { weekday: 6, label: "Sábado" },
  { weekday: 0, label: "Domingo" },
];

const pad2 = (n) => String(n).padStart(2, "0");

const toDay = (value) => {
  if (dayjs.isDayjs(value)) return value.startOf("day");
  if (value instanceof Date) return dayjs(value).startOf("day");
  if (typeof value === "string") {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (m) return dayjs(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  }
  return dayjs(value).startOf("day");
};

// ---- Money / duration ----

export const formatMoney = (value) => {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return "$0";
  // Manual grouping: es-AR does not group 4-digit numbers ("9000").
  const grouped = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${n < 0 ? "-" : ""}$${grouped}`;
};

export const formatDuration = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

// ---- Dates (es-AR labels, independent of the dayjs global locale) ----

/** "jueves 08 de octubre" */
export const formatLongDate = (date) => {
  const d = toDay(date);
  return `${WEEKDAYS_LONG[d.day()]} ${pad2(d.date())} de ${MONTHS_LONG[d.month()]}`;
};

/** "jueves 08/10" */
export const formatShortDate = (date) => {
  const d = toDay(date);
  return `${WEEKDAYS_LONG[d.day()]} ${pad2(d.date())}/${pad2(d.month() + 1)}`;
};

/** "jue 08/10" */
export const formatPillDate = (date) => {
  const d = toDay(date);
  return `${WEEKDAYS_SHORT[d.day()]} ${pad2(d.date())}/${pad2(d.month() + 1)}`;
};

// ---- Time ----

export const timeToMinutes = (time) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time || ""));
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
};

/** addMinutesToTime("16:00", 105) -> "17:45" (wraps past midnight). */
export const addMinutesToTime = (time, minutes) => {
  const base = timeToMinutes(time);
  if (base === null) return "";
  const total = (((base + Math.round(Number(minutes) || 0)) % 1440) + 1440) % 1440;
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

/** "Mañana" (< 13:00), "Tarde" (< 18:00), "Noche". */
export const slotPeriod = (time) => {
  const min = timeToMinutes(time);
  if (min === null || min < 13 * 60) return "Mañana";
  if (min < 18 * 60) return "Tarde";
  return "Noche";
};

export const SLOT_PERIODS = ["Mañana", "Tarde", "Noche"];

// ---- Cart ----

export const cartTotals = (services = []) =>
  (services || []).reduce(
    (acc, s) => ({
      durationMinutes: acc.durationMinutes + (Number(s?.durationMinutes) || 0),
      price: acc.price + (Number(s?.price) || 0),
      deposit: acc.deposit + (Number(s?.deposit) || 0),
    }),
    { durationMinutes: 0, price: 0, deposit: 0 },
  );

export const payAtVenue = (total, deposit) =>
  Math.max((Number(total) || 0) - (Number(deposit) || 0), 0);

// ---- Variants and add-ons ----

const cuenta = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/** "3 opciones · 2 adicionales": the parts that are zero are left out; "" for a plain service. */
export const opcionesPill = (service) =>
  [
    tieneVariantes(service) ? cuenta(service.variants.length, "opción", "opciones") : "",
    tieneAdicionales(service) ? cuenta(service.addOns.length, "adicional", "adicionales") : "",
  ]
    .filter(Boolean)
    .join(" · ");

/** Meta line of a service row: "45 min a 1 h 15 min · Desde $12.000" with variants, "45 min · $8.000" otherwise. */
export const serviceMeta = (service) => {
  if (!tieneVariantes(service)) return `${formatDuration(service?.durationMinutes)} · ${formatMoney(service?.price)}`;
  const { min, max } = rangoDeDuracion(service);
  const prices = service.variants.map((v) => Number(v.price) || 0);
  const from = service?.priceFrom != null ? precioDesde(service) : Math.min(...prices);
  const to = service?.priceTo != null ? Number(service.priceTo) : Math.max(...prices);
  const duration = min === max ? formatDuration(min) : `${formatDuration(min)} a ${formatDuration(max)}`;
  return `${duration} · ${from < to ? "Desde " : ""}${formatMoney(from)}`;
};

/**
 * Cart as appointment lines, in the same shape the back answers: the service line (with its variant) and, right
 * after it, one line per chosen add-on (same serviceId, with addOnId). `choices` is `{ [serviceId]: { variantId, addOnIds } }`.
 */
export const lineasDeCarrito = (services = [], choices = {}) =>
  (services || []).flatMap((service) => {
    const serviceId = String(service._id);
    const choice = choices?.[serviceId] || {};
    const variant = buscarVariante(service, choice.variantId);
    const base = variant || service;
    const lines = [
      {
        serviceId,
        name: service.name,
        ...(variant ? { variantId: String(variant._id ?? variant.id), variantName: variant.name } : {}),
        durationMinutes: Number(base.durationMinutes) || 0,
        price: Number(base.price) || 0,
        deposit: variant ? Math.min(Number(service.deposit) || 0, Number(variant.price) || 0) : Number(service.deposit) || 0,
      },
    ];
    (service.addOns || [])
      .filter((a) => (choice.addOnIds || []).includes(idDeAdicional(a)))
      .forEach((a) =>
        lines.push({
          serviceId,
          name: a.name,
          addOnId: idDeAdicional(a),
          durationMinutes: Number(a.durationMinutes) || 0,
          price: Number(a.price) || 0,
          deposit: 0,
        }),
      );
    return lines;
  });

// ---- Services / professionals ----

export const groupServicesByCategory = (services = []) => {
  const groups = [];
  const index = new Map();
  (services || []).forEach((service) => {
    const category = (service?.category || "").trim() || "Otros";
    if (!index.has(category)) {
      const group = { category, services: [] };
      index.set(category, group);
      groups.push(group);
    }
    index.get(category).services.push(service);
  });
  return groups;
};

export const minServicePrice = (services = []) => {
  const prices = (services || [])
    .map((s) => Number(s?.price))
    .filter((n) => Number.isFinite(n) && n >= 0);
  return prices.length ? Math.min(...prices) : null;
};

const professionalServiceIds = (professional) =>
  (professional?.serviceIds || []).map(String);

/** Professionals that perform every service in `serviceIds`. */
export const eligibleProfessionals = (professionals = [], serviceIds = []) => {
  const wanted = (serviceIds || []).map(String);
  return (professionals || []).filter((p) => {
    const offered = professionalServiceIds(p);
    return wanted.every((id) => offered.includes(id));
  });
};

/** Cart services (objects) the professional does not perform ("No realiza X"). */
export const missingServicesFor = (professional, services = []) => {
  const offered = professionalServiceIds(professional);
  return (services || []).filter((s) => !offered.includes(String(s?._id)));
};

// ---- Opening hours ----

const isOpenRange = (h) => h?.enabled !== false && h?.open && h?.close;

/**
 * Seven rows Lunes..Domingo. Returns [] when the salon has no opening hours
 * configured (an empty array means "not configured", not "closed").
 */
export const openingHoursRows = (openingHours = [], today = new Date()) => {
  if (!Array.isArray(openingHours) || openingHours.length === 0) return [];
  const todayWeekday = toDay(today).day();
  return WEEK_ROWS.map(({ weekday, label }) => {
    const ranges = openingHours
      .filter((h) => Number(h?.weekday) === weekday && isOpenRange(h))
      .sort((a, b) => String(a.open).localeCompare(String(b.open)))
      .map((h) => `${h.open} a ${h.close}`);
    return {
      weekday,
      label,
      closed: ranges.length === 0,
      text: ranges.length ? ranges.join(" y ") : "Cerrado",
      isToday: weekday === todayWeekday,
    };
  });
};

/**
 * "Abierto hoy hasta HH:MM" / "Cerrado hoy" from the opening hours. Returns
 * null when no hours are configured (unknown, so no claim is made).
 */
export const todayOpenStatus = (openingHours = [], now = new Date()) => {
  if (!Array.isArray(openingHours) || openingHours.length === 0) return null;
  const weekday = now.getDay();
  const closes = openingHours
    .filter((h) => Number(h?.weekday) === weekday && isOpenRange(h))
    .map((h) => String(h.close))
    .sort();
  const lastClose = closes[closes.length - 1];
  const current = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  if (!lastClose || current >= lastClose) return { open: false, text: "Cerrado hoy" };
  return { open: true, text: `Abierto hoy hasta ${lastClose}` };
};

/**
 * A date can be picked when it is not in the past, inside the reservation
 * window, and (only when opening hours are configured) on an open weekday.
 */
export const isDateBookable = (date, salon, today = new Date()) => {
  const d = toDay(date);
  const start = toDay(today);
  if (!d.isValid() || d.isBefore(start, "day")) return false;
  const windowDays = Number(salon?.reservationWindowDays);
  if (Number.isFinite(windowDays) && windowDays >= 0 && d.isAfter(start.add(windowDays, "day"), "day")) {
    return false;
  }
  const hours = salon?.openingHours;
  if (Array.isArray(hours) && hours.length > 0) {
    const weekday = d.day();
    if (!hours.some((h) => Number(h?.weekday) === weekday && isOpenRange(h))) return false;
  }
  return true;
};

// ---- Salon presentation ----

const isFiniteNumber = (v) => v !== null && v !== "" && v !== undefined && Number.isFinite(Number(v));

/** Full address line: "Av. Libertador 100, Palermo, Buenos Aires". */
export const salonAddressLine = (salon) =>
  [salon?.address, salon?.cityid?.name, salon?.stateid?.name]
    .map((part) => (typeof part === "string" ? part.trim() : ""))
    .filter(Boolean)
    .join(", ");

/** Google Maps link from coordinates when present, else from the address. */
export const salonMapsUrl = (salon) => {
  if (isFiniteNumber(salon?.lat) && isFiniteNumber(salon?.lng)) {
    return `https://www.google.com/maps?q=${Number(salon.lat)},${Number(salon.lng)}`;
  }
  const line = salonAddressLine(salon);
  return line ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(line)}` : "";
};

/** Policy lines shown under "Políticas del local" (only what the back provides). */
export const salonPolicyLines = (salon) => {
  const lines = [];
  const days = Number(salon?.reservationWindowDays);
  if (Number.isFinite(days) && days > 0) {
    lines.push(`Los turnos se pueden reservar hasta con ${days} días de antelación.`);
  }
  if (salon?.cancellationAllowed === false || salon?.cancellationAllowed === "false") {
    lines.push("Este local no permite cancelaciones.");
  } else if (Number.isFinite(Number(salon?.cancellationPolicyHours)) && salon?.cancellationPolicyHours !== null) {
    lines.push(`Cancelación sin cargo hasta ${Number(salon.cancellationPolicyHours)} hs antes.`);
  }
  return lines;
};

// ---- Custom client fields asked at booking (salon.clientFields) ----

const isBlank = (value) => value === undefined || value === null || (typeof value === "string" && !value.trim());

/** Active definitions the salon asks at booking; anything else the back sent is ignored. */
export const bookingClientFields = (salon) =>
  (Array.isArray(salon?.clientFields) ? salon.clientFields : []).filter((f) => f && f.id && f.label && f.archived !== true);

/** `{ fieldId: message }` for the answers that are missing or do not fit the definition. Empty when fine. */
export const clientFieldErrors = (fields, answers = {}) => {
  const errors = {};
  fields.forEach((f) => {
    const value = answers[f.id];
    if (f.type === "checkbox") {
      if (f.required && value !== true) errors[f.id] = "Tenés que marcar esta opción.";
      return;
    }
    if (isBlank(value)) {
      if (f.required) errors[f.id] = "Completá este dato.";
      return;
    }
    if (f.type === "number" && !Number.isFinite(Number(value))) errors[f.id] = "Ingresá un número válido.";
    if (f.type === "select" && !(f.options || []).includes(value)) errors[f.id] = "Elegí una de las opciones.";
  });
  return errors;
};

/** `clientFieldAnswers` body value (`{ fieldId: value }` typed by field), or `undefined` when nothing was answered. */
export const clientFieldAnswersPayload = (fields, answers = {}) => {
  const out = {};
  fields.forEach((f) => {
    const value = answers[f.id];
    if (f.type === "checkbox") {
      if (typeof value === "boolean") out[f.id] = value;
      return;
    }
    if (isBlank(value)) return;
    out[f.id] = f.type === "number" ? Number(value) : typeof value === "string" ? value.trim() : value;
  });
  return Object.keys(out).length ? out : undefined;
};

/** Spanish message for the 400s the back answers on `clientFieldAnswers`; `""` for any other error. */
export const clientFieldErrorMessage = (code) => {
  if (code === "field_required") return "Falta completar un dato que el salón pide. Revisalo y volvé a intentar.";
  if (code === "invalid_field") return "Alguno de los datos adicionales no es válido. Revisalos y volvé a intentar.";
  return "";
};
